(() => {
  if (window.PmSwipe) return;

  const SLOP = 6;
  const VERTICAL_RATIO = 1.7;
  const COMMIT_RATIO = 0.55;
  const DEFAULT_THRESHOLD = 30;

  const release = (element, pointerId) => {
    if (pointerId == null || !element?.hasPointerCapture?.(pointerId)) return;
    try { element.releasePointerCapture(pointerId); } catch (_) {}
  };

  const suppressNextClick = (element) => {
    const block = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
    };
    element.addEventListener('click', block, { capture: true, once: true });
    window.setTimeout(() => element.removeEventListener('click', block, true), 420);
  };

  const prepareImage = (image) => {
    if (!image) return Promise.resolve();
    if (image.dataset.src && !image.getAttribute('src')) image.src = image.dataset.src;
    if (image.dataset.src) image.removeAttribute('data-src');
    image.loading = 'eager';
    return typeof image.decode === 'function' ? image.decode().catch(() => undefined) : Promise.resolve();
  };

  const prepareSlide = (slide) => {
    const images = slide?.matches?.('img') ? [slide] : [...(slide?.querySelectorAll?.('img') || [])];
    return Promise.all(images.map(prepareImage));
  };

  const prepareAdjacent = (slides, index, { loop = false } = {}) => {
    if (!slides?.length) return Promise.resolve();
    const count = slides.length;
    const indices = new Set([index]);
    if (loop) {
      indices.add((index - 1 + count) % count);
      indices.add((index + 1) % count);
    } else {
      if (index > 0) indices.add(index - 1);
      if (index < count - 1) indices.add(index + 1);
    }
    return Promise.all([...indices].map((slideIndex) => prepareSlide(slides[slideIndex])));
  };

  const bind = (element, options = {}) => {
    if (!element) return () => {};
    let state = null;
    let touchGuard = null;
    const threshold = Number(options.threshold) || DEFAULT_THRESHOLD;

    const classifyIntent = (gesture, dx, dy) => {
      const absX = Math.abs(dx);
      const absY = Math.abs(dy);
      if (Math.max(absX, absY) < SLOP) return 'pending';
      if (absY >= VERTICAL_RATIO * absX) {
        gesture.verticalSamples = (gesture.verticalSamples || 0) + 1;
        return gesture.verticalSamples >= 2 ? 'vertical' : 'pending';
      }
      gesture.verticalSamples = 0;
      return 'horizontal';
    };

    const reset = ({ cancelled = false, snap = true } = {}) => {
      const current = state;
      state = null;
      if (!current) return;
      release(element, current.id);
      delete element.dataset.pmSwipeIntent;
      if (snap) options.onCancel?.({ cancelled, dx: current.dx || 0, dy: current.dy || 0 });
    };

    const onPointerDown = (event) => {
      if (!event.isPrimary || (event.pointerType === 'mouse' && event.button !== 0) || options.disabled?.()) return;
      state = {
        id: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        dx: 0,
        dy: 0,
        intent: 'pending',
        verticalSamples: 0,
        captured: false
      };
      element.dataset.pmSwipeIntent = 'pending';
      options.onStart?.();
    };

    const onPointerMove = (event) => {
      if (!state || event.pointerId !== state.id) return;
      state.dx = event.clientX - state.startX;
      state.dy = event.clientY - state.startY;
      const absX = Math.abs(state.dx);
      const absY = Math.abs(state.dy);

      if (state.intent === 'pending') {
        const intent = classifyIntent(state, state.dx, state.dy);
        if (intent === 'pending') return;
        if (intent === 'vertical') {
          state.intent = 'vertical';
          element.dataset.pmSwipeIntent = 'vertical';
          options.onVertical?.();
          return;
        }
        state.intent = 'horizontal';
        element.dataset.pmSwipeIntent = 'horizontal';
        try {
          element.setPointerCapture?.(event.pointerId);
          state.captured = true;
        } catch (_) {}
        options.onHorizontalLock?.();
      }

      if (state.intent !== 'horizontal') return;
      if (event.cancelable) event.preventDefault();
      options.onDrag?.(state.dx, state.dy);
    };

    // `touch-action: pan-y pinch-zoom` preserves native vertical scrolling, but WebKit
    // and Chromium can still claim a broad diagonal after pointer lock. A matching
    // non-passive TouchEvent guard cancels only gestures already classified horizontal.
    const onTouchStart = (event) => {
      if (event.touches.length !== 1 || options.disabled?.()) {
        touchGuard = null;
        return;
      }
      const touch = event.touches[0];
      touchGuard = {
        id: touch.identifier,
        startX: touch.clientX,
        startY: touch.clientY,
        intent: 'pending',
        verticalSamples: 0
      };
    };

    const onTouchMove = (event) => {
      if (!touchGuard) return;
      const touch = [...event.touches].find((candidate) => candidate.identifier === touchGuard.id);
      if (!touch) return;
      const dx = touch.clientX - touchGuard.startX;
      const dy = touch.clientY - touchGuard.startY;
      if (touchGuard.intent === 'pending') touchGuard.intent = classifyIntent(touchGuard, dx, dy);
      // Never cancel an undecided or vertical-intent move. In iOS/WebKit,
      // cancelling even the first vertical sample can suppress native page
      // scrolling for the remainder of the touch sequence. Only take control
      // once the gesture has positively resolved as horizontal.
      if (touchGuard.intent === 'horizontal' && event.cancelable) event.preventDefault();
    };

    const endTouchGuard = (event) => {
      if (!touchGuard) return;
      const ended = [...event.changedTouches].some((touch) => touch.identifier === touchGuard.id);
      if (ended || event.touches.length === 0) touchGuard = null;
    };

    const onPointerUp = (event) => {
      if (!state || event.pointerId !== state.id) return;
      state.dx = event.clientX - state.startX;
      state.dy = event.clientY - state.startY;
      const current = state;
      state = null;
      release(element, current.id);
      delete element.dataset.pmSwipeIntent;

      const committed = current.intent === 'horizontal'
        && Math.abs(current.dx) >= threshold
        && Math.abs(current.dx) >= COMMIT_RATIO * Math.abs(current.dy);
      if (committed) {
        suppressNextClick(element);
        options.onCommit?.(current.dx < 0 ? 1 : -1, current);
      } else {
        options.onCancel?.({ cancelled: false, dx: current.dx, dy: current.dy });
      }
    };

    const onPointerCancel = (event) => {
      if (!state || event.pointerId !== state.id) return;
      touchGuard = null;
      reset({ cancelled: true });
    };

    const onLostPointerCapture = (event) => {
      if (!state || event.pointerId !== state.id) return;
      // Mobile engines may transiently drop implicit/explicit capture while still
      // delivering the same active pointer. Losing capture never commits; keep the
      // gesture pending until the authoritative pointerup or pointercancel arrives.
      state.captured = false;
      options.onCaptureLost?.({ dx: state.dx, dy: state.dy });
    };

    element.addEventListener('pointerdown', onPointerDown, { passive: true });
    element.addEventListener('pointermove', onPointerMove, { passive: false });
    element.addEventListener('pointerup', onPointerUp, { passive: true });
    element.addEventListener('pointercancel', onPointerCancel, { passive: true });
    element.addEventListener('lostpointercapture', onLostPointerCapture, { passive: true });
    element.addEventListener('touchstart', onTouchStart, { passive: true });
    element.addEventListener('touchmove', onTouchMove, { passive: false });
    element.addEventListener('touchend', endTouchGuard, { passive: true });
    element.addEventListener('touchcancel', endTouchGuard, { passive: true });

    return () => {
      element.removeEventListener('pointerdown', onPointerDown);
      element.removeEventListener('pointermove', onPointerMove);
      element.removeEventListener('pointerup', onPointerUp);
      element.removeEventListener('pointercancel', onPointerCancel);
      element.removeEventListener('lostpointercapture', onLostPointerCapture);
      element.removeEventListener('touchstart', onTouchStart);
      element.removeEventListener('touchmove', onTouchMove);
      element.removeEventListener('touchend', endTouchGuard);
      element.removeEventListener('touchcancel', endTouchGuard);
      touchGuard = null;
      reset({ cancelled: true, snap: false });
    };
  };

  window.PmSwipe = Object.freeze({
    SLOP,
    VERTICAL_RATIO,
    COMMIT_RATIO,
    DEFAULT_THRESHOLD,
    bind,
    prepareImage,
    prepareSlide,
    prepareAdjacent
  });
})();



